import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Users, Heart, Bell, MessageCircle, BookOpen, Bookmark, ShieldCheck, UserPlus, UserMinus, ExternalLink, Send, Eye, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Admin() {
  const { user } = useApp();
  const [stats, setStats] = useState(null);
  const [supporters, setSupporters] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantProvider, setGrantProvider] = useState("stripe");
  const [granting, setGranting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, sup, us] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/supporters"),
        api.get("/admin/users", { params: { limit: 200 } }),
      ]);
      setStats(s.data);
      setSupporters(sup.data);
      setUsers(us.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        // not admin — will be gated below
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user?.is_admin]);

  // Redirect non-admins
  if (user && !user.is_admin) return <Navigate to="/home" replace />;

  const grant = async (e) => {
    e?.preventDefault?.();
    if (!grantEmail.trim()) return;
    setGranting(true);
    try {
      const res = await api.post("/admin/supporters/grant", {
        email: grantEmail.trim(),
        provider: grantProvider,
      });
      toast.success(`${res.data.name || res.data.email} activado como sostenedor ✓`);
      setGrantEmail("");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "user_not_found") {
        toast.error("No existe una cuenta con ese correo. Pídele que inicie sesión con Google primero.");
      } else if (detail === "admin_only") {
        toast.error("Solo el administrador puede usar esto.");
      } else {
        toast.error("Error al activar sostenedor");
      }
    } finally {
      setGranting(false);
    }
  };

  const revoke = async (email) => {
    if (!confirm(`Quitar estado de sostenedor a ${email}?`)) return;
    try {
      await api.post("/admin/supporters/revoke", { email });
      toast.success("Sostenedor removido");
      await load();
    } catch {
      toast.error("Error al remover");
    }
  };

  const statCards = stats
    ? [
        { icon: Users, label: "Usuarios totales", value: stats.users_total, hint: "Todos los sign-ups con Google" },
        { icon: Heart, label: "Sostenedores", value: stats.supporters, hint: "Con pago activo" },
        { icon: Bell, label: "Suscriptores push", value: stats.push_subscribers, hint: "Notificaciones activas" },
        { icon: MessageCircle, label: "Publicaciones", value: stats.posts, hint: "Comunidad" },
        { icon: BookOpen, label: "Oraciones", value: stats.prayers, hint: `${stats.answered_prayers} respondidas` },
        { icon: Bookmark, label: "Marcadores", value: stats.bookmarks, hint: "Versos guardados" },
      ]
    : [];

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <div>
        <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)]">FaithCycle</p>
        <div className="flex items-center gap-3 mt-2">
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Panel del dueño</h1>
          <ShieldCheck className="w-6 h-6 text-[var(--accent)]" strokeWidth={1.5} />
        </div>
        <p className="text-[var(--text-secondary)] mt-2 max-w-2xl">
          Solo tú ves esta página. Aquí controlas sostenedores y ves los números reales del app.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {statCards.map((c, i) => (
          <div
            key={i}
            data-testid={`stat-${c.label}`}
            className="rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-5"
          >
            <div className="flex items-center gap-2 text-[var(--text-secondary)]">
              <c.icon className="w-4 h-4 text-[var(--accent)]" strokeWidth={1.5} />
              <p className="uppercase tracking-widest text-[10px]">{c.label}</p>
            </div>
            <p className="font-serif text-4xl mt-3">{loading ? "—" : c.value}</p>
            <p className="text-xs text-[var(--text-secondary)] mt-1">{c.hint}</p>
          </div>
        ))}
      </div>

      {/* Grant supporter */}
      <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-6 max-w-2xl">
        <div className="flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />
          <h2 className="font-serif text-2xl tracking-tight">Activar sostenedor manualmente</h2>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mt-2">
          Úsalo cuando alguien pagó en Stripe o PayPal pero el webhook no dispara y no aparece como sostenedor.
          El correo debe coincidir con el de su cuenta Google en el app.
        </p>
        <form onSubmit={grant} className="mt-5 flex flex-col md:flex-row gap-3">
          <input
            data-testid="grant-email-input"
            type="email"
            required
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            placeholder="correo@example.com"
            className="flex-1 h-12 rounded-xl border border-[var(--border)] bg-[var(--bg)] px-4 focus:outline-none focus:border-[var(--accent)] transition-colors"
          />
          <select
            data-testid="grant-provider-select"
            value={grantProvider}
            onChange={(e) => setGrantProvider(e.target.value)}
            className="h-12 rounded-xl border border-[var(--border)] bg-[var(--bg)] px-4 focus:outline-none focus:border-[var(--accent)] transition-colors"
          >
            <option value="stripe">Stripe</option>
            <option value="paypal">PayPal</option>
            <option value="manual">Otro / cortesía</option>
          </select>
          <Button
            type="submit"
            data-testid="grant-submit"
            disabled={granting || !grantEmail.trim()}
            className="h-12 px-6 rounded-xl bg-[var(--accent)] text-white hover:bg-[var(--accent)]/90"
          >
            {granting ? "Activando…" : "Activar"}
          </Button>
        </form>
      </div>

      {/* Supporters list */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl tracking-tight">Sostenedores activos ({supporters.length})</h2>
          <a
            href="https://dashboard.stripe.com/subscriptions"
            target="_blank"
            rel="noreferrer"
            className="text-xs uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1"
          >
            Stripe <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
          </a>
        </div>
        <div className="rounded-2xl border border-[var(--border)] overflow-hidden">
          {supporters.length === 0 && !loading && (
            <p className="p-6 text-[var(--text-secondary)] text-sm">Aún no hay sostenedores.</p>
          )}
          {supporters.map((s, i) => (
            <div
              key={s.user_id}
              data-testid={`supporter-${s.email}`}
              className={`flex items-center justify-between gap-4 p-4 ${i > 0 ? "border-t border-[var(--border)]" : ""}`}
            >
              <div className="min-w-0">
                <p className="font-medium truncate">{s.name || s.email}</p>
                <p className="text-xs text-[var(--text-secondary)] truncate">
                  {s.email} · {s.supporter_provider || "—"} ·{" "}
                  {s.supporter_since ? new Date(s.supporter_since).toLocaleDateString("es-ES") : "—"}
                </p>
              </div>
              <button
                onClick={() => revoke(s.email)}
                className="text-xs uppercase tracking-widest text-[var(--text-secondary)] hover:text-red-500 flex items-center gap-1 transition-colors"
              >
                <UserMinus className="w-3.5 h-3.5" strokeWidth={1.5} />
                Quitar
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Broadcast email */}
      <BroadcastCard />

      {/* Study cache pre-warm */}
      <PrewarmCard />

      {/* All users */}
      <div>
        <h2 className="font-serif text-2xl tracking-tight mb-4">Últimos usuarios registrados ({users.length})</h2>
        <div className="rounded-2xl border border-[var(--border)] overflow-hidden max-h-[500px] overflow-y-auto">
          {users.map((u, i) => (
            <div
              key={u.user_id}
              className={`flex items-center justify-between gap-4 p-3 ${i > 0 ? "border-t border-[var(--border)]" : ""}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate">
                  {u.name} {u.is_supporter && <span className="text-[10px] uppercase tracking-widest text-[var(--accent)] ml-2">★ Sostenedor</span>}
                </p>
                <p className="text-xs text-[var(--text-secondary)] truncate">
                  {u.email} · {u.created_at ? new Date(u.created_at).toLocaleDateString("es-ES") : "—"}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BroadcastCard() {
  const DOMAIN_TEMPLATE = {
    subject: "El link de FaithCycle cambió a faithcycle.net",
    body_html: `<p>Te aviso que <strong>el enlace de FaithCycle cambió</strong> de <em>faithcycle.org</em> a <strong>faithcycle.net</strong>. Lo único que cambió es la terminación (<code>.org</code> → <code>.net</code>). Tu cuenta, tus versos guardados, tus oraciones y tu suscripción siguen intactos.</p><p>Discúlpame la confusión y gracias por acompañarme en este camino.</p>`,
  };
  const [subject, setSubject] = useState(DOMAIN_TEMPLATE.subject);
  const [body, setBody] = useState(DOMAIN_TEMPLATE.body_html);
  const [audience, setAudience] = useState("all");
  const [audienceCount, setAudienceCount] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/broadcast/audience", { params: { audience } });
        setAudienceCount(res.data.count);
      } catch {
        setAudienceCount(null);
      }
    })();
  }, [audience]);

  const preview = async () => {
    try {
      const res = await api.post("/admin/broadcast/preview", {
        subject,
        body_html: body,
        audience,
      });
      setPreviewHtml(res.data.html);
    } catch {
      toast.error("No se pudo generar preview");
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) return toast.error("Escribe un correo de prueba");
    setSending(true);
    try {
      const res = await api.post("/admin/broadcast/send", {
        subject,
        body_html: body,
        audience: "test",
        test_email: testEmail.trim(),
      });
      toast.success(`Correo de prueba enviado a ${testEmail} ✓`);
      setLastResult(res.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d === "resend_not_configured" || d === "gmail_not_configured") toast.error("Falta configurar el servicio de correo en variables de entorno");
      else toast.error("Error al enviar: " + (d || "desconocido"));
    } finally {
      setSending(false);
    }
  };

  const sendBroadcast = async () => {
    setSending(true);
    try {
      const res = await api.post("/admin/broadcast/send", {
        subject,
        body_html: body,
        audience,
      });
      setLastResult(res.data);
      toast.success(`Enviados: ${res.data.sent} · Fallidos: ${res.data.failed}`);
      setConfirm(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d === "resend_not_configured" || d === "gmail_not_configured") toast.error("Falta configurar el servicio de correo en variables de entorno");
      else if (d === "no_recipients") toast.error("No hay destinatarios");
      else toast.error("Error al enviar: " + (d || "desconocido"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-6" data-testid="broadcast-card">
      <div className="flex items-center gap-2">
        <Send className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl tracking-tight">Enviar correo a usuarios</h2>
      </div>
      <p className="text-sm text-[var(--text-secondary)] mt-2">
        Escribe un anuncio y envíalo a todos los usuarios o solo sostenedores. Vía Resend (~2 correos/segundo).
      </p>

      <div className="mt-5 space-y-4">
        <div>
          <label className="uppercase tracking-widest text-[10px] text-[var(--text-secondary)]">Asunto</label>
          <input
            data-testid="broadcast-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-1 w-full h-12 rounded-xl border border-[var(--border)] bg-[var(--bg)] px-4"
          />
        </div>
        <div>
          <label className="uppercase tracking-widest text-[10px] text-[var(--text-secondary)]">Cuerpo (HTML permitido)</label>
          <textarea
            data-testid="broadcast-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={7}
            className="mt-1 w-full rounded-xl border border-[var(--border)] bg-[var(--bg)] p-3 text-sm font-mono"
          />
          <p className="text-[10px] text-[var(--text-secondary)] mt-1">
            El saludo "Hola [nombre]," y la firma se añaden automáticamente.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="uppercase tracking-widest text-[10px] text-[var(--text-secondary)]">Enviar a</label>
            <select
              data-testid="broadcast-audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="mt-1 w-full h-12 rounded-xl border border-[var(--border)] bg-[var(--bg)] px-4"
            >
              <option value="all">Todos los usuarios ({audienceCount ?? "…"})</option>
              <option value="supporters">Solo sostenedores ({audience === "supporters" ? audienceCount ?? "…" : "…"})</option>
            </select>
          </div>
          <Button
            onClick={preview}
            variant="outline"
            className="h-12 px-4 rounded-xl border-[var(--border)]"
          >
            <Eye className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Preview
          </Button>
        </div>

        {previewHtml && (
          <div className="rounded-xl border border-[var(--border)] overflow-hidden">
            <div className="flex items-center justify-between p-2 bg-[var(--bg)] border-b border-[var(--border)]">
              <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] pl-2">Vista previa</p>
              <button onClick={() => setPreviewHtml("")} className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--accent)] pr-2">
                Cerrar
              </button>
            </div>
            <iframe title="preview" srcDoc={previewHtml} className="w-full h-[420px] bg-white" />
          </div>
        )}

        <div className="flex flex-wrap gap-3 items-end pt-2 border-t border-[var(--border)]">
          <div className="flex-1 min-w-[200px]">
            <label className="uppercase tracking-widest text-[10px] text-[var(--text-secondary)]">Correo de prueba (recomendado antes de enviar a todos)</label>
            <input
              data-testid="broadcast-test-email"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="tu@correo.com"
              className="mt-1 w-full h-12 rounded-xl border border-[var(--border)] bg-[var(--bg)] px-4"
            />
          </div>
          <Button
            data-testid="broadcast-test-btn"
            onClick={sendTest}
            disabled={sending || !testEmail.trim()}
            variant="outline"
            className="h-12 px-4 rounded-xl border-[var(--accent)] text-[var(--accent)]"
          >
            {sending ? "Enviando…" : "Enviar prueba"}
          </Button>
        </div>

        {!confirm ? (
          <Button
            data-testid="broadcast-send-btn"
            onClick={() => setConfirm(true)}
            disabled={sending || !subject.trim() || !body.trim()}
            className="w-full h-12 rounded-xl bg-[var(--accent)] text-white hover:bg-[var(--accent)]/90"
          >
            <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Enviar a {audienceCount ?? "…"} usuarios
          </Button>
        ) : (
          <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-4 space-y-3">
            <p className="text-sm">
              <strong>¿Confirmas?</strong> Este envío es <strong>irreversible</strong>. Se enviarán ~{audienceCount ?? "?"} correos vía Resend.
            </p>
            <div className="flex gap-2">
              <Button
                data-testid="broadcast-confirm-btn"
                onClick={sendBroadcast}
                disabled={sending}
                className="h-11 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white"
              >
                {sending ? "Enviando…" : "Sí, enviar ahora"}
              </Button>
              <Button
                onClick={() => setConfirm(false)}
                variant="outline"
                className="h-11 px-4 rounded-xl border-[var(--border)]"
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {lastResult && (
          <div className="rounded-xl border border-[var(--border)] bg-[var(--bg)] p-4 text-sm" data-testid="broadcast-result">
            <p><strong>Último envío:</strong> {lastResult.sent} enviados · {lastResult.failed} fallidos</p>
            {lastResult.errors?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-[var(--text-secondary)] text-xs">Ver errores ({lastResult.errors.length})</summary>
                <ul className="mt-2 text-xs space-y-1">
                  {lastResult.errors.map((e, i) => (
                    <li key={i}><code>{e.email}</code>: {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PrewarmCard() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/prewarm-cache/status");
      setStatus(res.data);
    } catch (_e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const run = async () => {
    if (running) return;
    setRunning(true);
    try {
      const res = await api.post("/admin/prewarm-cache");
      toast.success(`Pre-warm iniciado · ${res.data.verses} versículos × ${res.data.combinations_per_verse} combinaciones`);
      // Poll status every 5 s until running finishes (up to 5 minutes)
      let ticks = 0;
      const iv = setInterval(async () => {
        ticks += 1;
        try {
          const s = await api.get("/admin/prewarm-cache/status");
          setStatus(s.data);
          if (s.data.total !== undefined && s.data.total > 0) {
            // A finished run has counts (cached + warmed + skip + error). Presence of `warmed`, etc means it wrote a summary.
          }
        } catch (_e) {
          // ignore
        }
        if (ticks >= 60) clearInterval(iv); // stop after 5 minutes
      }, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start pre-warm");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[var(--border)] p-6 bg-[var(--surface-elevated)]" data-testid="prewarm-card">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl tracking-tight">Pre-warm del estudio</h2>
      </div>
      <p className="text-sm text-[var(--text-secondary)] mb-4">
        Genera de antemano las notas de estudio de los versículos más populares en los 3 modos
        (Estándar, Esencia, Palabra Clave) y en los 2 idiomas. Los versículos ya cacheados se saltan.
        Se ejecuta cada noche a las 03:00 UTC automáticamente.
      </p>

      {status && (
        <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Stat label="Cacheado (total)" value={status.total_cached_verses ?? 0} />
          <Stat label="Ya cacheado" value={status.cached ?? "—"} />
          <Stat label="Nuevos generados" value={status.warmed ?? "—"} />
          <Stat label="Última ejecución" value={status.timestamp ? new Date(status.timestamp).toLocaleString("es-ES") : "—"} />
        </div>
      )}

      <Button
        onClick={run}
        disabled={running}
        data-testid="prewarm-run-btn"
        className="bg-[var(--accent)] text-white hover:bg-[var(--accent)]/90"
      >
        {running ? "Iniciando..." : "Ejecutar ahora"}
      </Button>
      <button
        onClick={loadStatus}
        disabled={loading}
        className="ml-3 text-sm underline text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        data-testid="prewarm-refresh-btn"
      >
        {loading ? "Actualizando..." : "Actualizar estado"}
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-[var(--border)] p-3 bg-[var(--surface)]">
      <p className="uppercase tracking-widest text-[10px] text-[var(--text-secondary)]">{label}</p>
      <p className="text-lg font-medium truncate">{String(value)}</p>
    </div>
  );
}

